import { type OAuth2Tokens } from '@docyrus/api-client';

import { type DocyrusAuthConfig } from '../types';

import { DEFAULT_API_URL, SSO_TOKEN_PARAM } from '../constants';

/**
 * Handles SSO session token exchange for headless browser authentication.
 * Detects `?sso_token=` in the URL, exchanges it for OAuth2 tokens,
 * and cleans up the URL.
 */
export class SsoAuth {
  private config: DocyrusAuthConfig;
  constructor(config: DocyrusAuthConfig) {
    this.config = config;
  }
  /** Check if the current URL contains an SSO token parameter */
  hasSsoToken(): boolean {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);

    return params.has(SSO_TOKEN_PARAM);
  }
  /** Extract the SSO token from the URL */
  getSsoToken(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);

    return params.get(SSO_TOKEN_PARAM);
  }
  /** Exchange the SSO token for real OAuth2 tokens */
  async exchange(ssoToken: string): Promise<OAuth2Tokens> {
    const apiUrl = this.config.apiUrl ?? DEFAULT_API_URL;
    const { clientId } = this.config;

    if (!clientId) {
      throw new Error('clientId is required to exchange an SSO token');
    }

    const response = await fetch(`${apiUrl}/v1/oauth2/sso/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sso_token: ssoToken,
        client_id: clientId
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      throw new Error(error.error_description || error.message || 'SSO token exchange failed');
    }

    const data = await response.json();

    return {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: Math.floor(Date.now() / 1000) + data.expires_in,
      scope: data.scope
    };
  }
  /** Remove the sso_token parameter from the URL without triggering navigation */
  cleanUrl(): void {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);

    url.searchParams.delete(SSO_TOKEN_PARAM);
    window.history.replaceState({}, '', url.toString());
  }
}
